import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw } from "lucide-react";
import logoFull from "@/assets/orbit-logo.png";
import orbitO from "@/assets/orbit-o.png";
import wordmarkText from "@/assets/orbit-wordmark-text.png";

// Onde in basso — stessa forma/proporzioni del mockup. Con `lower` si traslano più in basso
// (stessa scala, nessuna distorsione: aggiungo spazio trasparente sopra e alzo il contenitore).
function Waves({ drift = false, lower = false }) {
  const shift = lower ? 120 : 0;
  const vb = 470 + shift;
  return (
    <div className={`absolute inset-x-0 bottom-0 ${lower ? "h-[69%]" : "h-[55%]"} overflow-hidden pointer-events-none`}>
      <motion.svg
        viewBox={`0 0 500 ${vb}`} preserveAspectRatio="none"
        className={`absolute bottom-0 left-0 h-full ${drift ? "w-[130%]" : "w-full"}`}
        animate={drift ? { x: ["0%", "-12%", "0%"] } : {}}
        transition={drift ? { duration: 9, ease: "easeInOut", repeat: Infinity } : {}}
      >
        <g transform={`translate(0,${shift})`}>
          <path d="M0,120 C70,95 120,152 210,138 C300,125 380,40 500,25 L500,470 L0,470 Z" fill="#E3EDF5" />
          <path d="M0,175 C90,150 165,118 235,165 C320,215 405,58 500,35 L500,470 L0,470 Z" fill="#CBDEEC" />
          <path d="M0,235 C60,258 145,302 245,272 C345,244 420,178 500,215 L500,470 L0,470 Z" fill="#A9C7DD" />
          <path d="M0,332 C85,300 175,326 252,356 C352,392 430,300 500,278 L500,470 L0,470 Z" fill="#7FA3BF" />
          <path d="M0,300 C95,332 185,402 285,376 C385,352 442,398 500,360 L500,470 L0,470 Z" fill="#557A95" />
        </g>
      </motion.svg>
    </div>
  );
}

// Stelle cadenti diagonali nella parte alta — appaiono per un attimo (una passata, non in loop).
function ShootingStars() {
  const stars = [
    { top: "6%", left: "-18%", delay: 0.3, len: 100 },
    { top: "18%", left: "-30%", delay: 0.6, len: 120 },
    { top: "3%", left: "12%", delay: 0.95, len: 80 },
    { top: "26%", left: "-10%", delay: 1.25, len: 90 },
  ];
  return (
    <div className="absolute inset-x-0 top-0 h-1/2 overflow-hidden pointer-events-none">
      {stars.map((s, i) => (
        <motion.div key={i} className="absolute" style={{ top: s.top, left: s.left }}
          initial={{ x: 0, y: 0, opacity: 0 }}
          animate={{ x: 300, y: 160, opacity: [0, 0.95, 0.95, 0] }}
          transition={{ duration: 0.85, delay: s.delay, ease: "easeIn" }}>
          <div className="relative" style={{ width: s.len, height: 2, borderRadius: 9999, transform: "rotate(28deg)", background: "linear-gradient(90deg, transparent, rgba(169,199,221,0.85) 65%, #FFFFFF)", boxShadow: "0 0 8px rgba(255,255,255,0.8)" }}>
            <span className="absolute" style={{ right: -2, top: -2, width: 5, height: 5, borderRadius: 9999, background: "#fff", boxShadow: "0 0 10px 2px rgba(255,255,255,0.9)" }} />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// La copertina vera e propria, con due varianti di animazione.
function Cover({ variant, onDone }) {

  useEffect(() => {
    const total = variant === "aurora" ? 2800 : variant === "motion" ? 2100 : 2600;
    const t = setTimeout(() => onDone && onDone(), total);
    return () => clearTimeout(t);
  }, [variant, onDone]);

  return (
    <motion.div
      className="absolute inset-0 worker-theme bg-[#F8FAFC] flex flex-col items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeInOut" } }}
      transition={{ duration: 0.4 }}
    >
      {variant === "aurora" ? (
        <>
          {/* sfondo aurora lilla-azzurro (palette ufficiale) */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(160deg,#F7F6FD 0%,#EFF1FB 52%,#E9E6F5 100%)" }} />
          <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full blur-3xl opacity-60" style={{ background: "radial-gradient(circle,#C4B7E6,transparent 70%)" }} />
          <div className="absolute -bottom-10 right-0 h-52 w-52 rounded-full blur-3xl opacity-50" style={{ background: "radial-gradient(circle,#A9C7DD,transparent 70%)" }} />
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center">
              {[0, 1, 2].map((i) => (
                <motion.span key={i} className="absolute rounded-full border" style={{ width: 150, height: 150, borderColor: "#C4B7E6" }}
                  initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: [0.5, 1.9], opacity: [0.5, 0] }}
                  transition={{ duration: 2.6, ease: "easeOut", repeat: Infinity, delay: i * 0.55 }} />
              ))}
            </div>
            <motion.div className="relative overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1, y: [0, -4, 0] }}
              transition={{ opacity: { duration: 0.7 }, scale: { duration: 0.7, ease: "easeOut" }, y: { duration: 3.2, ease: "easeInOut", repeat: Infinity, delay: 0.7 } }}>
              <img src={logoFull} alt="Orbit" className="h-24 w-auto object-contain select-none" draggable="false" />
              <motion.div className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.65) 50%, transparent 65%)" }}
                initial={{ x: "-120%" }} animate={{ x: "120%" }}
                transition={{ duration: 1.1, ease: "easeInOut", delay: 0.9 }} />
            </motion.div>
          </div>
        </>
      ) : (
        <>
          {/* alone morbido */}
          <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 50% 38%, rgba(224,242,254,0.9), transparent 55%)" }} />
          <Waves drift={variant === "motion"} lower={variant === "stars"} />
          {variant === "stars" && <ShootingStars />}
          {variant === "fade" || variant === "stars" ? (
        // OPZIONE 1 — Fade morbido: logo nella posizione corretta (O + scritta insieme),
        // ruota SOLO la "O" di ~90° in senso orario; poi respiro discreto dell'intero lockup.
        <motion.div
          className="relative"
          style={{ width: "252px", height: "97px" }}
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: [0.99, 1.025, 1] }}
          transition={{ opacity: { duration: 0.55, ease: "easeOut" }, scale: { duration: 3, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" } }}
        >
          <motion.img
            src={orbitO} alt="" draggable="false"
            className="absolute object-contain select-none"
            style={{ left: "0%", top: "0%", width: "32.9%" }}
            initial={{ rotate: -90 }}
            animate={{ rotate: 0 }}
            transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
          />
          <img
            src={wordmarkText} alt="Orbit — Digital Ecosystem" draggable="false"
            className="absolute object-contain select-none"
            style={{ left: "37.4%", top: "0%", width: "62.6%" }}
          />
        </motion.div>
      ) : (
        // OPZIONE 2 — Movimento leggero: ingresso dinamico + lieve bob continuo
        <motion.div
          className="relative flex flex-col items-center"
          initial={{ opacity: 0, y: 18, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 120, damping: 14, mass: 0.8 }}
        >
          <motion.img
            src={logoFull} alt="Orbit" className="h-24 w-auto object-contain select-none" draggable="false"
            animate={{ y: [0, -6, 0], rotate: [0, 1.5, 0] }}
            transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
          />
        </motion.div>
      )}
        </>
      )}
    </motion.div>
  );
}

// Piccola anteprima Home (solo scenografia per mostrare il fade out).
function HomeGlimpse() {
  return (
    <motion.div
      className="absolute inset-0 worker-theme bg-[#EDF0F4] p-5"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="h-8 w-8 rounded-lg bg-[#E0F2FE] flex items-center justify-center">
          <img src={logoFull} alt="" className="h-5 w-auto object-contain" />
        </span>
        <div>
          <p className="text-base font-bold text-[#22364D] leading-tight">Ciao Mario</p>
          <p className="text-xs text-[#557A95]">Bentornato su Orbit</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {["Registra una prestazione", "I miei Registri", "Assenze e permessi", "Agenda Settimanale"].map((t) => (
          <div key={t} className="rounded-xl bg-white border border-[#C2D2E1] p-3 shadow-sm">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] mb-2" />
            <p className="text-xs font-semibold text-[#22364D] leading-tight">{t}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function PhonePanel({ title, subtitle, variant }) {
  const [phase, setPhase] = useState("cover"); // cover | home
  const [key, setKey] = useState(0);
  const replay = () => { setPhase("cover"); setKey((k) => k + 1); };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-center">
        <p className="font-heading text-lg font-bold text-slate-900">{title}</p>
        <p className="text-sm text-slate-500 max-w-xs">{subtitle}</p>
      </div>

      {/* Cornice telefono */}
      <div className="relative w-[300px] h-[600px] rounded-[2.5rem] border-[10px] border-slate-800 bg-black shadow-2xl overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-800 rounded-b-2xl z-20" />
        <div className="absolute inset-0 rounded-[1.8rem] overflow-hidden">
          <AnimatePresence mode="wait">
            {phase === "cover" ? (
              <Cover key={`cover-${key}`} variant={variant} onDone={() => setPhase("home")} />
            ) : (
              <HomeGlimpse key={`home-${key}`} />
            )}
          </AnimatePresence>
        </div>
      </div>

      <button onClick={replay}
        className="inline-flex items-center gap-2 rounded-lg bg-[#22364D] text-white text-sm font-semibold px-4 py-2 hover:bg-[#2c4560] transition-colors">
        <RefreshCw className="h-4 w-4" /> Rigioca animazione
      </button>
    </div>
  );
}

export default function CoverPreview() {
  return (
    <div className="min-h-screen bg-[#F1F5F9] py-10 px-4">
      <div className="max-w-5xl mx-auto text-center mb-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Anteprima copertina post-login</h1>
        <p className="text-slate-500 max-w-2xl mx-auto">Breve transizione mostrata <b>solo dopo un login riuscito</b>, prima della Home. Due proposte a confronto — usa "Rigioca" per rivedere ciascuna animazione.</p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 justify-items-center">
        <PhonePanel
          title="Opzione 1 — Fade morbido (approvata)"
          subtitle="Logo già nella posizione corretta (O + scritta insieme): ruota solo la “O” di ~90° in senso orario, poi respiro discreto. Onde in basso in palette Lavoratore."
          variant="fade"
        />
        <PhonePanel
          title="Opzione 4 — Stelle cadenti"
          subtitle="Come l'Opzione 1 (stessa “O” rotante) ma con le onde traslate più in basso e, nella parte alta, alcune stelle cadenti che attraversano in diagonale la schermata per un attimo."
          variant="stars"
        />
      </div>

      <div className="max-w-3xl mx-auto mt-12 rounded-xl bg-white border border-slate-200 p-5 text-sm text-slate-600">
        <p className="font-semibold text-slate-800 mb-1">Differenze in sintesi</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Opzione 1 (Fade morbido)</b>: logo fermo nella posizione corretta con la scritta; ruota solo la “O” di ~90° in senso orario, poi respiro lento. Onde in palette Lavoratore. Approvata.</li>
          <li><b>Opzione 4 (Stelle cadenti)</b>: identica alla 1 ma con le onde traslate più in basso (stesse proporzioni) e, in alto, alcune stelle cadenti diagonali che compaiono per un attimo. Tocco più suggestivo.</li>
        </ul>
      </div>
    </div>
  );
}
